import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { getSession, startRealtimeSession, submitAnswers } from '../services/api';
import { useTranscription } from '../contexts/TranscriptionContext';
import VoiceControlPanel from '../components/VoiceControlPanel';
import SurveyForm from '../components/SurveyForm';
import '../styles/RealtimeSession.css';

const RealtimeSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const {
    connected,
    joinSession,
    leaveSession,
    startTranscription,
    sendWebRTCAudio,
    processTranscription,
    onTranscription,
    onAnswersFilled,
    onRealtimeResult,
    onProcessingStatus,
    onError
  } = useTranscription();
  
  const [session, setSession] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [processingTranscription, setProcessingTranscription] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [audioUploadHistory, setAudioUploadHistory] = useState([]);
  const [realtimeTranscription, setRealtimeTranscription] = useState('');
  const [recentlyFilledAnswers, setRecentlyFilledAnswers] = useState([]);
  
  // For WebRTC audio recording
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  
  // Handle Socket.io events
  useEffect(() => {
    if (!connected) {
      console.log("WebSocket not connected, waiting for connection...");
      return;
    }
    
    console.log(`WebSocket connected, joining session: ${sessionId}`);
    
    // Try to join the session room multiple times if needed
    let retryCount = 0;
    const maxRetries = 3;
    
    const attemptJoin = () => {
      // Only attempt if we haven't hit the max retries
      if (retryCount >= maxRetries) {
        console.error(`Failed to join session after ${maxRetries} attempts`);
        return;
      }
      
      retryCount++;
      console.log(`Attempt ${retryCount} to join session: ${sessionId}`);
      
      // Join the session room
      joinSession(sessionId);
      setIsSocketReady(true);
    };
    
    // Initial attempt
    attemptJoin();
    
    // Listen for transcription updates
    const unsubscribeTranscription = onTranscription((data) => {
      if (data.session_id === sessionId) {
        console.log("Received transcription update");
        setTranscription(data.transcription);
      }
    });
    
    // Listen for filled answers
    const unsubscribeAnswers = onAnswersFilled((data) => {
      if (data.session_id === sessionId) {
        console.log("Received answers update");
        const newAnswers = { ...answers };
        const newlyFilledQuestions = [];
        
        data.answers.forEach(answer => {
          if (newAnswers[answer.question_id] !== answer.value) {
            newlyFilledQuestions.push(answer.question_id);
          }
          newAnswers[answer.question_id] = answer.value;
        });
        
        setAnswers(newAnswers);
        
        // Track recently filled answers for highlighting
        if (newlyFilledQuestions.length > 0) {
          setRecentlyFilledAnswers(newlyFilledQuestions);
          // Clear highlighting after 5 seconds
          setTimeout(() => {
            setRecentlyFilledAnswers([]);
          }, 5000);
        }
        setProcessingTranscription(false);
      }
    });
    
    // Listen for realtime results (combined transcription + answers)
    const unsubscribeRealtime = onRealtimeResult((data) => {
      if (data.session_id === sessionId) {
        console.log("Received realtime result");
        // Update transcription - use only the last chunk to avoid duplication
        setTranscription(data.last_chunk || data.transcription);
        
        // Also update realtime transcription with just the new chunk
        setRealtimeTranscription(data.last_chunk || data.transcription);
        
        // Update answers
        const newAnswers = { ...answers };
        const newlyFilledQuestions = [];
        
        data.answers.forEach(answer => {
          if (newAnswers[answer.question_id] !== answer.value) {
            newlyFilledQuestions.push(answer.question_id);
          }
          newAnswers[answer.question_id] = answer.value;
        });
        
        setAnswers(newAnswers);
        
        // Track recently filled answers for highlighting
        if (newlyFilledQuestions.length > 0) {
          setRecentlyFilledAnswers(newlyFilledQuestions);
          // Clear highlighting after 5 seconds
          setTimeout(() => {
            setRecentlyFilledAnswers([]);
          }, 5000);
        }
        setProcessingTranscription(false);
        setProcessingStatus('');
      }
    });

    // Listen for processing status updates
    const unsubscribeProcessingStatus = onProcessingStatus((data) => {
      if (data.session_id === sessionId) {
        console.log("Processing status update:", data.status, data.message);
        setProcessingStatus(data.message);
        
        if (data.status === 'processing' || data.status === 'transcribing' || data.status === 'filling_answers') {
          setProcessingTranscription(true);
        } else if (data.status === 'complete') {
          setProcessingTranscription(false);
          setProcessingStatus('');
        }
      }
    });

    // Listen for errors
    const unsubscribeError = onError((data) => {
      if (data.session_id === sessionId) {
        console.error("Socket error:", data.message);
        setError(data.message);
        setProcessingTranscription(false);
        setProcessingStatus('');
      }
    });
    
    // Cleanup on unmount
    return () => {
      console.log("Cleaning up WebSocket listeners");
      leaveSession();
      unsubscribeTranscription();
      unsubscribeAnswers();
      unsubscribeRealtime();
      unsubscribeProcessingStatus();
      unsubscribeError();
    };
  }, [connected, sessionId, joinSession, leaveSession, 
      onTranscription, onAnswersFilled, onRealtimeResult, onProcessingStatus, onError, answers]);
  
  // Load session data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log(`Fetching session data for ID: ${sessionId}`);
        const sessionData = await getSession(sessionId);
        console.log('Session data retrieved:', sessionData);
        
        // Check if we got a valid session response with survey
        if (!sessionData || !sessionData.survey_id || !sessionData.survey) {
          console.error('Invalid session data received:', sessionData);
          setError('Session data is invalid or missing required fields');
          setSession(null);
          setSurvey(null);
          return;
        }
        
        // Set session data
        setSession(sessionData);
        
        // Extract survey data
        setSurvey(sessionData.survey);
        
        // Initialize answers from existing data
        const initialAnswers = {};
        if (sessionData.answers) {
          sessionData.answers.forEach(answer => {
            initialAnswers[answer.question_id] = answer.value;
          });
        }
        setAnswers(initialAnswers);
        
        // Initialize real-time session with backend
        console.log('Initializing realtime session with backend');
        const realtimeResponse = await startRealtimeSession(sessionId);
        console.log('Realtime session response:', realtimeResponse);
        
        // Make sure the realtime session was initialized correctly
        if (!realtimeResponse || realtimeResponse.status !== 'ok') {
          console.error('Failed to initialize realtime session:', realtimeResponse);
          setError('Failed to initialize realtime connection. Please try again.');
          return;
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching session details:', err);
        // Get more detailed error information
        let errorMessage = 'Failed to load session. Please try again later.';
        
        if (err.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Server error response:', err.response.data);
          console.error('Status code:', err.response.status);
          errorMessage = `Server error: ${err.response.status}. ${err.response.data?.error || 'Unknown error'}`;
          
          // Set session and survey to null to trigger the "not found" UI
          setSession(null);
          setSurvey(null);
        } else if (err.request) {
          // The request was made but no response was received
          console.error('No response received from server');
          errorMessage = 'No response from server. Please check if the backend is running on port 8001.';
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error setting up request:', err.message);
          errorMessage = `Error: ${err.message}`;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Cleanup on unmount
    return () => {
      stopRecording();
    };
  }, [sessionId]); // stopRecording is defined in this component and doesn't need to be in dependencies

  // Request microphone access
  const requestMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return stream;
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. Please ensure microphone access is allowed.');
      return null;
    }
  };

  // Start recording
  const startRecording = async () => {
    if (!isSocketReady) {
      setError('WebSocket connection not ready. Please try again.');
      return;
    }
    
    try {
      // Initialize real-time transcription session
      console.log('Starting transcription for session:', sessionId);
      await startTranscription(sessionId);
      
      // Request microphone access
      const stream = await requestMicrophoneAccess();
      if (!stream) {
        return;
      }
      
      // Clear previous transcriptions
      setTranscription('');
      setRealtimeTranscription('');
      
      // Set up audio recorder with explicit format
      audioChunks.current = [];
      
      // Try different audio formats in order of preference
      // Prioritize M4A since our backend testing shows it works perfectly
      let selectedMimeType = 'audio/webm'; // default fallback
      
      // Log all supported formats for debugging
      const formatTests = [
        'audio/mp4',
        'audio/mp4; codecs=mp4a.40.2', 
        'audio/wav',
        'audio/ogg; codecs=opus',
        'audio/ogg',
        'audio/webm; codecs=opus',
        'audio/webm'
      ];
      
      console.log('🔍 Testing MediaRecorder format support:');
      formatTests.forEach(format => {
        const supported = MediaRecorder.isTypeSupported(format);
        console.log(`  ${format}: ${supported ? '✅' : '❌'}`);
      });
      
      // Try formats in order of preference (prioritize WebM with Opus for real-time compatibility)
      if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        selectedMimeType = 'audio/webm; codecs=opus';
        console.log('🎙️ Using WebM with Opus codec (best real-time compatibility)');
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        selectedMimeType = 'audio/webm';
        console.log('🎙️ Using WebM format (good real-time compatibility)');
      } else if (MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')) {
        selectedMimeType = 'audio/ogg; codecs=opus';
        console.log('🎙️ Using OGG with Opus codec');
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        selectedMimeType = 'audio/ogg';
        console.log('🎙️ Using OGG format');
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        selectedMimeType = 'audio/wav';
        console.log('🎙️ Using WAV format (good compatibility)');
      } else if (MediaRecorder.isTypeSupported('audio/mp4; codecs=mp4a.40.2')) {
        selectedMimeType = 'audio/mp4; codecs=mp4a.40.2';
        console.log('🎙️ Using MP4 with AAC codec (may fragment)');
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        selectedMimeType = 'audio/mp4';
        console.log('🎙️ Using MP4 format (may fragment)');
      } else {
        console.log('🎙️ Using default format - may need conversion');
      }
      
      const options = selectedMimeType !== 'audio/webm' ? { mimeType: selectedMimeType } : {};
      mediaRecorder.current = new MediaRecorder(stream, options);
      
      // Set up event handlers for real-time chunking
      mediaRecorder.current.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          console.log('Received audio chunk:', event.data.size, 'bytes');
          
          // Add to chunks for final processing
          audioChunks.current.push(event.data);
          
          // Only process chunks that are large enough to be valid audio files
          // Small chunks (< 4KB) are likely incomplete and will cause format errors
          if (event.data.size < 4096) {
            console.log(`Chunk too small (${event.data.size} bytes), skipping processing to avoid format errors`);
            return;
          }
          
          // Create a complete recording from all chunks to ensure proper headers
          // MediaRecorder WebM chunks after the first lack proper headers
          console.log(`Creating complete WebM from ${audioChunks.current.length} chunks`);
          
          // Create a new complete recording with all chunks so far
          const completeRecording = new Blob(audioChunks.current, { type: selectedMimeType });
          
          // Determine correct file extension based on MIME type
          let fileExtension = 'webm'; // default - works well with backend
          if (selectedMimeType.includes('webm')) {
            fileExtension = 'webm';
          } else if (selectedMimeType.includes('ogg')) {
            fileExtension = 'ogg';
          } else if (selectedMimeType.includes('wav')) {
            fileExtension = 'wav';
          } else if (selectedMimeType.includes('mp4')) {
            fileExtension = 'm4a';
          }
          
          try {
            console.log('Processing complete recording chunk...');
            console.log(`Complete recording size: ${completeRecording.size} bytes, MIME: ${selectedMimeType}, extension: ${fileExtension}`);
            
            const result = await sendWebRTCAudio(completeRecording, sessionId, fileExtension);
            
            if (result && result.id) {
              console.log('🔥 Processing complete recording for real-time transcription:', result.id);
              
              // Process the chunk for transcription and AI answer filling
              const processResult = await processUploadedAttachment(result.id);
              
              // Add to upload history
              const timestamp = new Date().toLocaleTimeString();
              setAudioUploadHistory(prev => [...prev, {
                timestamp,
                filename: result.filename,
                size: completeRecording.size,
                result: result,
                status: 'completed',
                isChunk: true,
                chunkNumber: audioChunks.current.length
              }]);
            }
          } catch (error) {
            console.error('Error processing complete recording chunk:', error);
            // Add to upload history with error status
            const timestamp = new Date().toLocaleTimeString();
            setAudioUploadHistory(prev => [...prev, {
              timestamp,
              filename: 'chunk_error',
              size: completeRecording.size,
              result: null,
              status: 'error',
              error: error.message,
              isChunk: true,
              chunkNumber: audioChunks.current.length
            }]);
          }
        }
      };
      
      // Handle recording stop - final cleanup only since we process chunks in real-time
      mediaRecorder.current.onstop = async () => {
        console.log('MediaRecorder stopped - recording session complete');
        console.log(`Processed ${audioChunks.current.length} chunks during this session`);
        
        // Clear audio chunks
        audioChunks.current = [];
        
        // Final transcription is already accumulated in realtimeTranscription
        console.log('📝 Final accumulated transcription length:', realtimeTranscription.length);
      };
      
      // Start recording with automatic chunking using timeslice
      // Use 30 seconds to ensure audio files have proper headers and structure
      // Longer chunks are more likely to be complete and valid
      mediaRecorder.current.start(30000); // 30 second timeslice
      setIsRecording(true);
      
      console.log('🎙️ Started continuous recording with automatic 30-second chunking');
      
    } catch (err) {
      console.error('Error starting real-time session:', err);
      // More detailed error message
      if (err.message) {
        setError(`Failed to start recording: ${err.message}`);
      } else {
        setError('Failed to start recording. Please check browser console for details.');
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    console.log('🎙️ Stopping continuous recording...');
    
    // Stop media recorder
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      console.log('🎙️ Stopping MediaRecorder and processing final audio...');
      mediaRecorder.current.stop();
      
      // Stop all tracks in the stream
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorder.current = null;
    }
    
    // Update recording state
    setIsRecording(false);
    
    // Process the transcription if we have one
    if (transcription) {
      handleProcessTranscription();
    }
  };

  // Refresh session answers from the backend
  const refreshSessionAnswers = async () => {
    try {
      console.log('🔄 Refreshing session answers from backend...');
      const sessionData = await getSession(sessionId);
      
      if (sessionData && sessionData.answers) {
        // Convert answers array to object for easy lookup
        const newAnswers = {};
        const newlyFilledQuestions = [];
        
        sessionData.answers.forEach(answer => {
          // Check if this is a new or updated answer
          if (answers[answer.question_id] !== answer.value) {
            newlyFilledQuestions.push(answer.question_id);
          }
          newAnswers[answer.question_id] = answer.value;
        });
        
        // Update state with new answers
        setAnswers(newAnswers);
        
        // Highlight newly filled answers
        if (newlyFilledQuestions.length > 0) {
          console.log(`🎯 Highlighting ${newlyFilledQuestions.length} newly filled answers:`, newlyFilledQuestions);
          setRecentlyFilledAnswers(newlyFilledQuestions);
          
          // Clear highlighting after 5 seconds
          setTimeout(() => {
            setRecentlyFilledAnswers([]);
          }, 5000);
        }
      }
    } catch (error) {
      console.error('Error refreshing session answers:', error);
    }
  };

  // Process uploaded attachment for transcription
  const processUploadedAttachment = async (attachmentId) => {
    console.log(`🔥🔥🔥 STARTING processUploadedAttachment for ${attachmentId} 🔥🔥🔥`);
    setProcessingTranscription(true);
    setProcessingStatus('Transcribing audio...');
    
    try {
      console.log(`🔥 Processing attachment ${attachmentId} for transcription`);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://deva-survey-api.promptcatalog.online';
      const processUrl = `${apiUrl}/sessions/attachments/${attachmentId}/process?sync=true`;
      console.log(`🔥 Calling: ${processUrl}`);
      
      // Call the attachment processing endpoint with sync=true for immediate results
      const response = await fetch(processUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log(`🔥 Process response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`🔥 Process response error: ${errorText}`);
        throw new Error(`Processing failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('🔥 Processing result:', result);
      
      if (result.status === 'success') {
        // Get the attachment details with transcription
        const attachmentResponse = await fetch(`${apiUrl}/sessions/attachments/${attachmentId}?include_transcription=true`);
        
        if (attachmentResponse.ok) {
          const attachmentData = await attachmentResponse.json();
          console.log('📝 Attachment with transcription:', attachmentData);
          
          if (attachmentData.transcription) {
            // Show only the latest transcription chunk, not accumulated
            setRealtimeTranscription(attachmentData.transcription);
            setTranscription(attachmentData.transcription); // Show only current chunk
            setProcessingStatus('Filling answers from transcription...');
            
            // If answers were filled, refresh the session data to get updated answers
            if (result.filled_answers > 0) {
              console.log(`🎉 Successfully filled ${result.filled_answers} answers from transcription`);
              
              // Fetch updated session data to get the newly filled answers
              await refreshSessionAnswers();
            }
          } else {
            setError('No transcription was generated from the audio');
          }
        }
      } else {
        setError(`Processing failed: ${result.error || 'Unknown error'}`);
      }
      
    } catch (err) {
      console.error('Error processing attachment:', err);
      setError(`Failed to process audio: ${err.message}`);
    } finally {
      setProcessingTranscription(false);
      setProcessingStatus('');
    }
  };

  // Process the transcription with AI
  const handleProcessTranscription = async () => {
    if (!transcription) return;
    
    setProcessingTranscription(true);
    
    try {
      // Send transcription to server for processing
      await processTranscription(transcription, sessionId);
      
      // Note: The actual results will come back through the WebSocket events
      // So we don't need to update answers here
    } catch (err) {
      console.error('Error processing transcription:', err);
      setProcessingTranscription(false);
      setError('Failed to process transcription. Please try again.');
    }
  };

  // Handle manual answer changes
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Remove from recently filled if user manually edits
    setRecentlyFilledAnswers(prev => prev.filter(id => id !== questionId));
  };

  // Save answers and navigate back to session detail
  const saveAndExit = async () => {
    try {
      // Format answers for API
      const formattedAnswers = Object.keys(answers).map(questionId => ({
        question_id: questionId,
        value: answers[questionId]
      }));
      
      await submitAnswers(sessionId, formattedAnswers);
      navigate(`/sessions/${sessionId}`);
      
    } catch (err) {
      console.error('Error saving answers:', err);
      setError('Failed to save answers. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert variant="danger">
          {error}
        </Alert>
        <div className="mt-3 text-muted small">
          <p>Debug Information:</p>
          <ul>
            <li>Session ID: {sessionId}</li>
                    <li>API URL: {process.env.REACT_APP_API_URL || 'https://deva-survey-api.promptcatalog.online'}</li>
            <li>WebSocket Connected: {connected ? 'Yes' : 'No'}</li>
          </ul>
          <p>If the API URL doesn't match the port where your backend is running, 
          update the REACT_APP_API_URL in frontend/.env or restart with the correct configuration.</p>
        </div>
      </div>
    );
  }

  if (!session || !survey) {
    return (
      <div>
        <Alert variant="warning">
          Session not found. It may have been deleted or the ID is incorrect.
        </Alert>
        
        <Button 
          variant="primary" 
          className="mt-3"
          onClick={() => navigate('/surveys')}
        >
          Return to Surveys List
        </Button>
        
        <div className="mt-3 text-muted small">
          <p>Debug Information:</p>
          <ul>
            <li>Session ID: {sessionId}</li>
            <li>API URL: {process.env.REACT_APP_API_URL || 'https://deva-survey-api.promptcatalog.online'}</li>
            <li>WebSocket Connected: {connected ? 'Yes' : 'No'}</li>
          </ul>
          <p>If the API URL doesn't match the port where your backend is running, 
          update the REACT_APP_API_URL in frontend/.env or restart with the correct configuration.</p>
          
          <div className="mt-3">
            <p>Try these troubleshooting steps:</p>
            <ol>
              <li>Check that your database contains the session (run debug_db.py)</li>
              <li>Verify the session ID in the URL matches an existing session</li>
              <li>Make sure the frontend and backend are configured to use the same port</li>
              <li>Restart both frontend and backend using start.sh</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="container-fluid p-0" style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <div className="bg-light border-bottom p-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">Real-time Voice Input</h4>
            <small className="text-muted">
              Session: {session.name || `${session.id.slice(0, 8)}`}
            </small>
          </div>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => isRecording ? setShowConfirmModal(true) : navigate(`/sessions/${sessionId}`)}
          >
            Back to Session
          </Button>
        </div>
      </div>
      
      {/* Two-column layout */}
      <div className="row g-0" style={{ height: 'calc(100vh - 80px)' }}>
        
        {/* Left Column - Survey Form (scrollable) */}
        <div className="col-md-7" style={{ height: '100%', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
          <div className="p-4">
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">📝 Survey Questions</h5>
                <small className="text-muted">Edit answers manually or let voice input fill them automatically</small>
              </Card.Header>
              <Card.Body>
                <SurveyForm 
                  survey={survey}
                  answers={answers}
                  onAnswerChange={handleAnswerChange}
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveAndExit();
                  }}
                  submitting={false}
                  showSubmitButton={true}
                  submitButtonText="💾 Save All Answers and Exit"
                  submitButtonVariant="success"
                  submitButtonSize="lg"
                  recentlyFilledAnswers={recentlyFilledAnswers}
                  emptyStateMessage="No answers filled yet. Start recording to automatically fill answers, or manually enter responses below."
                  emptyStateIcon={<i className="bi bi-mic-fill fs-1 mb-3 d-block"></i>}
                  cardClassName="mb-4"
                />
              </Card.Body>
            </Card>
          </div>
        </div>
        
        {/* Right Column - Voice Controls (fixed) */}
        <div className="col-md-5 border-start" style={{ height: '100%', overflowY: 'auto', backgroundColor: 'white' }}>
          <div className="p-3">
            <VoiceControlPanel 
              connected={connected}
              isRecording={isRecording}
              startRecording={startRecording}
              stopRecording={stopRecording}
              transcription={transcription}
              realtimeTranscription={realtimeTranscription}
              processingTranscription={processingTranscription}
              processingStatus={processingStatus}
              audioUploadHistory={audioUploadHistory}
            />
          </div>
        </div>
        
      </div>
      
      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Stop Recording?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You are currently recording. If you leave now, your current recording will be lost.
          Do you want to stop recording and save the answers?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Continue Recording
          </Button>
          <Button variant="primary" onClick={stopRecording}>
            Stop and Process
          </Button>
          <Button variant="danger" onClick={() => navigate(`/sessions/${sessionId}`)}>
            Discard and Exit
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RealtimeSession;
